
<template>
  <div
    :id="id"
    class="map"
  />
</template>

<script>
/**
 * MapExplore is the main map panel and most of the functionality comes from turf
 * This component just handles 'mapbox layers' on the map and knows nothing about our 'datasets' which may contain
 * multiple layers
 * Key events:
 *   map-create-layer: creates a single map layer with given id
 *   map-hide-layers: hide the layer with provided id (must already have been created)
 */
import axios from 'axios'
import LayersControl from '../helpers/layers-control'
import { getFirstForegroundLayerId } from '../helpers/map-helpers'
import { getCountryExtentByISO3 } from '../helpers/request-helpers'
import mixinAddLayers from '../mixins/mixin-add-layers'
import { fetchRasterPixelCategory } from '../../../helpers/request-helpers'

const MAP_CONTAINER_ID = 'map-target'

export default {
  mixins: [mixinAddLayers],
  /* eslint-disable no-undef */
  props: {
    search: {
      type: Boolean,
      default: false
    },
    toggleId: {
      type: String,
      default: ''
    },
    modalId: {
      type: String,
      required: true
    },
    allowNoSelectedDataset: {
      type: Boolean,
      default: true
    },
    mapboxToken: {
      type: String,
      required: true
    },
    zoomToIso: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      id: MAP_CONTAINER_ID,
      mapbox: {
        accessToken: this.mapboxToken,
        geocodingUrl: 'https://api.mapbox.com/geocoding/v5/mapbox.places'
      },
      firstForegroundLayerId: '',
      options: {
        container: MAP_CONTAINER_ID,
        style: 'mapbox://styles/unepwcmc/cjoa5g1k910bb2rpezelvlu9k',
        pitchWithRotate: false,
        center: [0, 30],
        zoom: 1,
      }
    }
  },

  mounted() {
    this.$eventHub.$on('map-create-and-show-layers', this.addLayers)
    this.$eventHub.$on('map-show-layers', this.showLayers)
    this.$eventHub.$on('map-hide-layers', this.hideLayers)

    if (this.zoomToIso) {
      this.initBoundingBoxAndMap()
    } else {
      this.initMap()
    }
  },

  methods: {
    initBoundingBoxAndMap () {
      getCountryExtentByISO3(this.zoomToIso, res => {
        const extent = res.data.extent
        const padding = 5

        this.options.bounds = [
          [extent.xmin - padding, extent.ymin - padding],
          [extent.xmax + padding, extent.ymax + padding]
        ]

        this.initMap()
      })
    },

    initMap () {
      mapboxgl.accessToken = this.mapbox.accessToken

      const map = new mapboxgl.Map(this.options)

      this.map = map

      /** This event is for when mapbox style is changed on e.g. to 'streets' or 'basic'
       * so we reload all layers on the new map
       */
      map.on('style.load', () => {
        map.resize() //fixes chrome bug where canvas initally renders with default size
        this.setFirstForegroundLayerId()
        this.$eventHub.$emit('map-reload-layers', map.isStyleLoaded())
      })

      map.on('load', () => {
        const navControl = new mapboxgl.NavigationControl()
        const layersControl = new LayersControl()

        /** mapBox specific controls */
        map.addControl(layersControl, 'bottom-left')
        map.addControl(navControl, 'bottom-left')
        this.setFirstForegroundLayerId()
        // correctTabFlow(this.$el)
        this.$eventHub.$emit('map-load')

        if(this.search) { this.addSearchControl() }
      })

      if(this.toggleId) {
        map.on('click', (e) => { this.clickMap(e) })
      }
    },

    clickMap (e) {
      if(this.$store.state.toggle[this.toggleId]) {
        this.$eventHub.$emit(`modal-open-${this.modalId}`)
        this.$store.dispatch('form/updateLatLng', e.lngLat)
        this.getCountry(`${e.lngLat.lng},${e.lngLat.lat}`)
      }
      // Currently disabled due to lack of raster lookup service
      // else {
      //   this.addPopup(e)
      // }
    },

    addPopup (e) {
      const layerSlug = this.$store.state.map.selectedLayerSlug
      const isCategorical = this.$store.state.map.isSelectedLayerCategorical

      if (isCategorical) {
        fetchRasterPixelCategory(
          { layerSlug, lngLat: e.lngLat },
          res => {
            if (res.data) {
              this.addPopupToMap({lngLat: e.lngLat, pixelCategory: res.data})
            }
          }
        )
      }
    },

    addPopupToMap ({lngLat, pixelCategory}) {
      let html = `
        <span class="mapboxgl-popup__key" style="width: 20px;height:20px;background-color:${pixelCategory.color};"></span>
        <span class="mapboxgl-popup__text">${pixelCategory.name}</span>
      `

      new mapboxgl.Popup()
        .setLngLat(lngLat)
        .setHTML(html)
        .addTo(this.map)
    },

    setFirstForegroundLayerId () {
      this.firstForegroundLayerId = getFirstForegroundLayerId(this.map)
    },

    addLayers(layers) {
      layers.forEach(l => { this.addLayer(l) })
    },

    addLayer(layer) {
      let options = {
        'id': 'habitat',
        'type': 'raster',
        'minzoom': 0,
        'maxzoom': 22,
        'source': {
          'type': 'raster',
          'tiles': [layer.mapbox.tileset],
          'tileSize': 64
        },
        'paint': {
          'raster-hue-rotate': 0
        }
      }

      this.map.addLayer(options)
    },

    showLayers(layerIds) {
      this.setLayerVisibilities(layerIds, true)
    },

    hideLayers(layerIds) {
      this.setLayerVisibilities(layerIds, false)
    },

    setLayerVisibilities(layerIds, isVisible) {
      layerIds.forEach(id => {
        this.setLayerVisibility(id, isVisible)
      })
    },

    setLayerVisibility(layerId, isVisible) {
      const visibility = isVisible ? 'visible' : 'none'

      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, 'visibility', visibility)
      }
    },

    addSearchControl () {
      const geocoderControl = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        flyTo: true,
        zoom: false,
        marker: false,
        mapboxgl: mapboxgl
      })

      this.map.addControl(geocoderControl, 'top-left')
    },

    getCountry (lngLat) {
      axios
        .get(`${this.mapbox.geocodingUrl}/${lngLat}.json?types=country&access_token=${this.mapbox.accessToken}`)
        .then(response => {
          const firstFeature = response.data.features[0]
          const iso_2 = firstFeature ? firstFeature.properties.short_code.toUpperCase() : ''

          if (iso_2) {
            axios
              .get(`/countries/${iso_2}.json`)
              .then(res => {
                this.$store.dispatch('form/updateCountry', res.data.name)
                this.$store.dispatch('form/updateRegion', res.data.region.name)
              })
          } else {
            this.$store.dispatch('form/updateCountry', '')
            this.$store.dispatch('form/updateRegion', '')
          }
        })
    }
  }
}
</script>
